import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { authService } from '../api/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const resolveLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return url;
};

function normalizeSlug(text) {
  return (text || '')
    .toLowerCase()
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^a-z0-9-]/g, '')
    .replaceAll(/-+/g, '-');
}

const initialFiltroOpticas = {
  busqueda: '',
  activo: 'todos',
  page: 1,
  page_size: 10,
};

const initialFiltroAuditoria = {
  busqueda: '',
  accion: '',
  optica_id: '',
  page: 1,
  page_size: 20,
};

const initialFormUsuarioOptica = {
  email: '',
  nombre_completo: '',
  contraseña: '',
  rol: 'operario',
};

const initialFormPago = {
  optica_id: '',
  monto: '',
  moneda: 'ARS',
  plan_codigo: 'basico',
  periodo: '',
  metodo_pago: 'transferencia',
  comprobante_nro: '',
  notas: '',
  renovar_dias: 30,
};

const formatearFecha = (fechaStr) => {
  if (!fechaStr) return '-';
  try {
    const d = new Date(fechaStr);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return fechaStr;
  }
};

const calcularAntiguedad = (fechaStr) => {
  if (!fechaStr) return '-';
  try {
    const ahora = new Date();
    const fecha = new Date(fechaStr);
    const diffMs = ahora - fecha;
    const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (dias < 1) return 'Hoy';
    if (dias === 1) return 'Hace 1 día';
    if (dias < 30) return `Hace ${dias} días`;
    const meses = Math.floor(dias / 30);
    const diasRest = dias % 30;
    if (meses === 1) return diasRest > 0 ? `Hace 1 m. y ${diasRest} d.` : 'Hace 1 mes';
    return diasRest > 0 ? `Hace ${meses} m. y ${diasRest} d.` : `Hace ${meses} meses`;
  } catch {
    return '-';
  }
};

const calcularDiasRestantes = (fechaVenceStr) => {
  if (!fechaVenceStr) return null;
  try {
    const ahora = new Date();
    const vence = new Date(fechaVenceStr);
    const diffMs = vence - ahora;
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const MODULOS_FALLBACK = [
  { codigo: 'dashboard', nombre: 'Dashboard', grupo: 'principal' },
  { codigo: 'clientes', nombre: 'Clientes', grupo: 'principal' },
  { codigo: 'lista_precios', nombre: 'Lista de Precios', grupo: 'principal' },
  { codigo: 'nuevo_cliente', nombre: 'Nuevo Cliente', grupo: 'principal' },
  { codigo: 'estado_anteojos', nombre: 'Estado de anteojos', grupo: 'operacion' },
  { codigo: 'herramientas_cristales', nombre: 'Cristales', grupo: 'herramientas' },
  { codigo: 'herramientas_stock_armazones', nombre: 'Stock Armazones', grupo: 'herramientas' },
  { codigo: 'mensajeria', nombre: 'Mensajeria', grupo: 'herramientas' },
  { codigo: 'finanzas', nombre: 'Finanzas', grupo: 'finanzas' },
  { codigo: 'calendario_pagos', nombre: 'Calendario de pagos', grupo: 'finanzas' },
  { codigo: 'facturacion', nombre: 'Facturacion', grupo: 'finanzas' },
  { codigo: 'balance', nombre: 'Balance', grupo: 'finanzas' },
];

const SuperAdminDashboard = () => {
  const [tab, setTab] = useState('opticas');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  const [metricas, setMetricas] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [modulosDisponibles, setModulosDisponibles] = useState([]);
  const [matrizDraft, setMatrizDraft] = useState({});
  const [matrizDirty, setMatrizDirty] = useState(false);
  const [opticasData, setOpticasData] = useState({ items: [], total: 0, page: 1, page_size: 10, total_pages: 1 });
  const [auditoriaData, setAuditoriaData] = useState({ items: [], total: 0, page: 1, page_size: 20, total_pages: 1 });
  const [usuariosOptica, setUsuariosOptica] = useState([]);

  // Estados de Cobranzas y Pagos
  const [pagosData, setPagosData] = useState({ items: [], total: 0, page: 1, page_size: 20, metricas: null });
  const [loadingPagos, setLoadingPagos] = useState(false);
  const [filtroPagoOpticaId, setFiltroPagoOpticaId] = useState('');
  const [modalRegistrarPago, setModalRegistrarPago] = useState(false);
  const [formPago, setFormPago] = useState(initialFormPago);

  const [filtroOpticas, setFiltroOpticas] = useState(initialFiltroOpticas);
  const [filtroAuditoria, setFiltroAuditoria] = useState(initialFiltroAuditoria);
  const [opticaGestionUsuariosId, setOpticaGestionUsuariosId] = useState('');

  const [formOptica, setFormOptica] = useState({ nombre: '', slug: '' });
  const [formAdmin, setFormAdmin] = useState({ optica_id: '', email: '', nombre_completo: '', contraseña: '' });
  const [formUsuarioOptica, setFormUsuarioOptica] = useState(initialFormUsuarioOptica);
  const [logoModalOptica, setLogoModalOptica] = useState(null);
  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [tabLogoUrlInput, setTabLogoUrlInput] = useState('');
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [selectedTabLogoFile, setSelectedTabLogoFile] = useState(null);
  const [logoScaleInput, setLogoScaleInput] = useState(1);
  const [tabLogoScaleInput, setTabLogoScaleInput] = useState(1.12);

  const fetchConAuth = async (url, options = {}) => {
    const authHeaders = authService.obtenerHeaders();
    if (options.body instanceof FormData) {
      delete authHeaders['Content-Type'];
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Error en la operación');
    }

    return response;
  };

  const cargarMetricas = async () => {
    const response = await fetchConAuth(`${API_URL}/api/superadmin/metricas`);
    setMetricas(await response.json());
  };

  const cargarPlanes = async () => {
    const response = await fetchConAuth(`${API_URL}/api/superadmin/planes`);
    const data = await response.json();
    setPlanes(data.planes || []);
    setModulosDisponibles(data.modulos_disponibles || []);
  };

  const cargarOpticas = async (custom = null) => {
    const f = custom || filtroOpticas;
    const params = new URLSearchParams();
    if (f.busqueda) params.set('busqueda', f.busqueda);
    if (f.activo !== 'todos') params.set('activo', f.activo === 'activo' ? 'true' : 'false');
    params.set('page', String(f.page));
    params.set('page_size', String(f.page_size));

    const response = await fetchConAuth(`${API_URL}/api/superadmin/opticas/paginado?${params.toString()}`);
    setOpticasData(await response.json());
  };

  const habilitarPlanDefinitivo = async (optica) => {
    const res = await Swal.fire({
      icon: 'question',
      title: `¿Habilitar plan comercial para ${optica.nombre}?`,
      text: 'Esta acción removerá el estado de prueba (Trial) y habilitará el acceso permanente de su plan comercial sin corte automático a los 3 meses.',
      showCancelButton: true,
      confirmButtonText: 'Sí, habilitar plan',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#059669',
    });
    if (!res.isConfirmed) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}/habilitar-plan-definitivo`, {
        method: 'POST',
      });
      Swal.fire({
        icon: 'success',
        title: 'Plan Definitivo Habilitado',
        text: `La óptica ${optica.nombre} ahora tiene acceso permanente habilitado.`,
        timer: 2000,
      });
      await cargarOpticas(filtroOpticas);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const extenderTrial = async (optica, dias = 30) => {
    const res = await Swal.fire({
      icon: 'question',
      title: `¿Extender prueba de ${optica.nombre}?`,
      text: `Se añadirán ${dias} días adicionales de prueba a partir del vencimiento actual o desde hoy.`,
      showCancelButton: true,
      confirmButtonText: `Sí, extender +${dias} días`,
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
    });
    if (!res.isConfirmed) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}/extender-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dias }),
      });
      Swal.fire({
        icon: 'success',
        title: 'Prórroga asignada',
        text: `Se extendió el período de prueba de ${optica.nombre} por ${dias} días.`,
        timer: 2000,
      });
      await cargarOpticas(filtroOpticas);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const cargarPagos = async (page = 1, opticaId = '') => {
    try {
      setLoadingPagos(true);
      const params = new URLSearchParams({ page: String(page), page_size: '20' });
      if (opticaId) params.append('optica_id', opticaId);
      const res = await fetchConAuth(`${API_URL}/api/superadmin/pagos?${params.toString()}`);
      const data = await res.json();
      setPagosData(data);
    } catch (err) {
      console.error('Error al cargar pagos:', err);
    } finally {
      setLoadingPagos(false);
    }
  };

  const abrirModalRegistrarPago = (opticaPreseleccionada = null) => {
    const fecha = new Date();
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const periodoSugerido = `${meses[fecha.getMonth()]} ${fecha.getFullYear()}`;

    setFormPago({
      optica_id: opticaPreseleccionada ? String(opticaPreseleccionada.id) : '',
      monto: '',
      moneda: 'ARS',
      plan_codigo: opticaPreseleccionada ? opticaPreseleccionada.plan_codigo : 'basico',
      periodo: periodoSugerido,
      metodo_pago: 'transferencia',
      comprobante_nro: '',
      notas: '',
      renovar_dias: 30,
    });
    setModalRegistrarPago(true);
  };

  const registrarPago = async (e) => {
    e.preventDefault();
    if (!formPago.optica_id) {
      Swal.fire({ icon: 'warning', title: 'Óptica requerida', text: 'Por favor seleccioná la óptica receptora del pago.' });
      return;
    }
    if (!formPago.monto || Number(formPago.monto) <= 0) {
      Swal.fire({ icon: 'warning', title: 'Monto inválido', text: 'Ingresá un monto válido mayor a 0.' });
      return;
    }
    if (!formPago.periodo.trim()) {
      Swal.fire({ icon: 'warning', title: 'Período requerido', text: 'Ingresá el período abonado (ej: Noviembre 2026).' });
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/pagos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          optica_id: Number(formPago.optica_id),
          monto: Number(formPago.monto),
          moneda: formPago.moneda,
          plan_codigo: formPago.plan_codigo,
          periodo: formPago.periodo.trim(),
          metodo_pago: formPago.metodo_pago,
          comprobante_nro: formPago.comprobante_nro ? formPago.comprobante_nro.trim() : null,
          notas: formPago.notas ? formPago.notas.trim() : null,
          renovar_dias: Number(formPago.renovar_dias) || 30,
        }),
      });

      Swal.fire({
        icon: 'success',
        title: '¡Pago Registrado!',
        text: `Se registró el cobro y se renovó la suscripción por ${formPago.renovar_dias} días exitosamente.`,
        timer: 2000,
      });

      setModalRegistrarPago(false);
      await cargarPagos(pagosData.page, filtroPagoOpticaId);
      await cargarOpticas(filtroOpticas);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al registrar pago', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const eliminarPago = async (pago) => {
    const res = await Swal.fire({
      icon: 'warning',
      title: '¿Eliminar este cobro?',
      text: `Se eliminará el registro de pago #${pago.id} por $${pago.monto.toLocaleString('es-AR')} de ${pago.optica_nombre}.`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });
    if (!res.isConfirmed) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/pagos/${pago.id}`, {
        method: 'DELETE',
      });
      Swal.fire({ icon: 'success', title: 'Pago eliminado', timer: 1500 });
      await cargarPagos(pagosData.page, filtroPagoOpticaId);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const cargarAuditoria = async (custom = null) => {
    const f = custom || filtroAuditoria;
    const params = new URLSearchParams();
    if (f.busqueda) params.set('busqueda', f.busqueda);
    if (f.accion) params.set('accion', f.accion);
    if (f.optica_id) params.set('optica_id', f.optica_id);
    params.set('page', String(f.page));
    params.set('page_size', String(f.page_size));

    const response = await fetchConAuth(`${API_URL}/api/superadmin/auditoria?${params.toString()}`);
    setAuditoriaData(await response.json());
  };

  const cargarTodo = async () => {
    try {
      setLoading(true);
      await Promise.all([cargarMetricas(), cargarPlanes(), cargarOpticas(), cargarAuditoria()]);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo cargar el panel superadmin' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarTodo();
  }, []);

  useEffect(() => {
    if (tab === 'cobranzas') {
      cargarPagos(1, filtroPagoOpticaId);
    }
  }, [tab, filtroPagoOpticaId]);

  const accionesDisponibles = useMemo(() => {
    const acciones = new Set((auditoriaData.items || []).map((i) => i.accion));
    return Array.from(acciones).sort((a, b) => a.localeCompare(b));
  }, [auditoriaData.items]);

  const planesOrdenados = useMemo(() => {
    const orden = ['basico', 'esferico', 'asferico_hd'];
    const rank = (codigo) => {
      const idx = orden.indexOf(codigo);
      return idx === -1 ? 999 : idx;
    };
    return [...planes].sort((a, b) => rank(a.codigo) - rank(b.codigo));
  }, [planes]);

  const modulosCatalogo = useMemo(() => {
    if (Array.isArray(modulosDisponibles) && modulosDisponibles.length > 0) {
      return modulosDisponibles;
    }

    const codigosDesdePlanes = new Set();
    (planes || []).forEach((plan) => {
      (plan.modulos_por_defecto || []).forEach((codigo) => codigosDesdePlanes.add(codigo));
    });

    if (codigosDesdePlanes.size === 0) {
      return MODULOS_FALLBACK;
    }

    const fallbackPorCodigo = new Map(MODULOS_FALLBACK.map((m) => [m.codigo, m]));
    return Array.from(codigosDesdePlanes).map((codigo) => {
      if (fallbackPorCodigo.has(codigo)) {
        return fallbackPorCodigo.get(codigo);
      }
      return {
        codigo,
        nombre: String(codigo).replaceAll('_', ' '),
        grupo: 'otros',
      };
    });
  }, [modulosDisponibles, planes]);

  const modulosOrdenados = useMemo(() => {
    const prioridadGrupo = {
      principal: 1,
      operacion: 2,
      herramientas: 3,
      finanzas: 4,
      ecommerce: 5,
      otros: 6,
    };

    return [...modulosCatalogo].sort((a, b) => {
      const ga = prioridadGrupo[a.grupo] || 99;
      const gb = prioridadGrupo[b.grupo] || 99;
      if (ga !== gb) return ga - gb;
      return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es');
    });
  }, [modulosCatalogo]);

  useEffect(() => {
    if (!Array.isArray(planes) || planes.length === 0) {
      setMatrizDraft({});
      setMatrizDirty(false);
      return;
    }

    const nextDraft = {};
    planes.forEach((plan) => {
      nextDraft[plan.codigo] = Array.isArray(plan.modulos_por_defecto)
        ? [...plan.modulos_por_defecto]
        : [];
    });
    setMatrizDraft(nextDraft);
    setMatrizDirty(false);
  }, [planes]);

  const moduloIncluidoEnPlan = (moduloCodigo, planCodigo) => {
    const listaDraft = matrizDraft?.[planCodigo];
    const lista = Array.isArray(listaDraft) ? listaDraft : [];
    return lista.includes(moduloCodigo);
  };

  const toggleModuloMatriz = (planCodigo, moduloCodigo) => {
    setMatrizDraft((prev) => {
      const actuales = new Set(Array.isArray(prev?.[planCodigo]) ? prev[planCodigo] : []);
      if (actuales.has(moduloCodigo)) {
        actuales.delete(moduloCodigo);
      } else {
        actuales.add(moduloCodigo);
      }

      return {
        ...prev,
        [planCodigo]: Array.from(actuales),
      };
    });
    setMatrizDirty(true);
  };

  const revertirMatriz = () => {
    const nextDraft = {};
    planes.forEach((plan) => {
      nextDraft[plan.codigo] = Array.isArray(plan.modulos_por_defecto)
        ? [...plan.modulos_por_defecto]
        : [];
    });
    setMatrizDraft(nextDraft);
    setMatrizDirty(false);
  };

  const guardarMatrizPlanes = async () => {
    const cambios = planes.filter((plan) => {
      const original = new Set(Array.isArray(plan.modulos_por_defecto) ? plan.modulos_por_defecto : []);
      const draft = new Set(Array.isArray(matrizDraft?.[plan.codigo]) ? matrizDraft[plan.codigo] : []);
      if (original.size !== draft.size) return true;
      for (const item of original) {
        if (!draft.has(item)) return true;
      }
      return false;
    });

    if (cambios.length === 0) {
      Swal.fire({ icon: 'info', title: 'Sin cambios', timer: 1000, showConfirmButton: false });
      setMatrizDirty(false);
      return;
    }

    try {
      setSaving(true);
      for (const plan of cambios) {
        const modulos = Array.isArray(matrizDraft?.[plan.codigo]) ? matrizDraft[plan.codigo] : [];
        if (modulos.length === 0) {
          throw new Error(`El plan ${plan.nombre} no puede quedar sin módulos.`);
        }
        const response = await fetchConAuth(`${API_URL}/api/superadmin/planes/${plan.codigo}/modulos`, {
          method: 'PUT',
          body: JSON.stringify({ modulos }),
        });
        const data = await response.json();
        setPlanes(data.planes || []);
        setModulosDisponibles(data.modulos_disponibles || []);
      }

      await Promise.all([cargarAuditoria(), cargarOpticas()]);
      setMatrizDirty(false);
      Swal.fire({ icon: 'success', title: 'Matriz guardada', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo guardar la matriz de módulos' });
    } finally {
      setSaving(false);
    }
  };

  const crearOptica = async (e) => {
    e.preventDefault();
    const nombre = formOptica.nombre.trim();
    const slug = normalizeSlug(formOptica.slug || nombre);

    if (!nombre || !slug) {
      Swal.fire({ icon: 'warning', title: 'Datos incompletos', text: 'Nombre y slug son obligatorios' });
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas`, {
        method: 'POST',
        body: JSON.stringify({ nombre, slug, activo: true, plan_codigo: 'basico' }),
      });
      setFormOptica({ nombre: '', slug: '' });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({ icon: 'success', title: 'Óptica creada', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo crear la óptica' });
    } finally {
      setSaving(false);
    }
  };

  const eliminarOptica = async (optica) => {
    if (optica.id === 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Acción protegida',
        text: 'La óptica principal (#1) no puede ser eliminada.',
      });
      return;
    }

    const { value: confirmText } = await Swal.fire({
      icon: 'warning',
      title: `¿Eliminar "${optica.nombre}"?`,
      html: `
        <div class="text-left text-sm space-y-2 text-slate-700">
          <p>Esta acción <strong>eliminará de forma permanente e irreversible</strong> la óptica <strong>#${optica.id} (${optica.nombre})</strong> junto con todos sus usuarios, clientes, anteojos, stock y registros.</p>
          <p class="text-xs text-red-600 font-semibold">Para confirmar, escribí el nombre exacto de la óptica o la palabra <strong>ELIMINAR</strong>:</p>
        </div>
      `,
      input: 'text',
      inputPlaceholder: `Escribí "${optica.nombre}" o ELIMINAR`,
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar definitivamente',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
      preConfirm: (val) => {
        const limpio = (val || '').trim().toLowerCase();
        if (limpio !== (optica.nombre || '').trim().toLowerCase() && limpio !== 'eliminar') {
          Swal.showValidationMessage(`Debés escribir "${optica.nombre}" o ELIMINAR para confirmar`);
          return null;
        }
        return true;
      },
    });

    if (!confirmText) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}`, {
        method: 'DELETE',
      });
      if (String(opticaGestionUsuariosId) === String(optica.id)) {
        setOpticaGestionUsuariosId('');
        setUsuariosOptica([]);
      }
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({
        icon: 'success',
        title: 'Óptica eliminada',
        text: `La óptica "${optica.nombre}" fue eliminada definitivamente.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar',
        text: e.message || 'No se pudo eliminar la óptica',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleEstadoOptica = async (optica) => {
    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}`, {
        method: 'PUT',
        body: JSON.stringify({ activo: !optica.activo }),
      });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo cambiar el estado' });
    } finally {
      setSaving(false);
    }
  };

  const editarOptica = async (optica) => {
    const { value: formValues } = await Swal.fire({
      title: `Editar óptica #${optica.id}`,
      html:
        `<input id="swal-nombre" class="swal2-input" placeholder="Nombre" value="${optica.nombre || ''}">` +
        `<input id="swal-slug" class="swal2-input" placeholder="Slug" value="${optica.slug || ''}">`,
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const nombre = document.getElementById('swal-nombre')?.value?.trim();
        const slugRaw = document.getElementById('swal-slug')?.value?.trim();
        const slug = normalizeSlug(slugRaw || nombre);
        if (!nombre || !slug) {
          Swal.showValidationMessage('Nombre y slug son obligatorios');
          return null;
        }
        return { nombre, slug };
      },
    });

    if (!formValues) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          nombre: formValues.nombre,
          slug: formValues.slug,
          activo: !!optica.activo,
        }),
      });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({ icon: 'success', title: 'Óptica actualizada', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo actualizar la óptica' });
    } finally {
      setSaving(false);
    }
  };

  const abrirModalLogo = (optica) => {
    setLogoModalOptica(optica);
    setLogoUrlInput(optica.logo_url || '');
    setTabLogoUrlInput(optica.logo_tab_url || optica.tab_logo_url || '');
    setLogoScaleInput(Number(optica.logo_scale || 1));
    setTabLogoScaleInput(Number(optica.logo_tab_scale || 1.12));
    setSelectedLogoFile(null);
    setSelectedTabLogoFile(null);
  };

  const cerrarModalLogo = () => {
    setLogoModalOptica(null);
    setLogoUrlInput('');
    setTabLogoUrlInput('');
    setLogoScaleInput(1);
    setTabLogoScaleInput(1.12);
    setSelectedLogoFile(null);
    setSelectedTabLogoFile(null);
  };

  const guardarLogoUrl = async () => {
    if (!logoModalOptica) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          logo_url: logoUrlInput.trim() || null,
          logo_tab_url: tabLogoUrlInput.trim() || null,
          logo_scale: Number(logoScaleInput || 1),
          logo_tab_scale: Number(tabLogoScaleInput || 1.12),
        }),
      });

      window.dispatchEvent(new CustomEvent('optica-logo-updated', {
        detail: {
          opticaId: logoModalOptica.id,
          logoUrl: logoUrlInput.trim() || null,
          tabLogoUrl: tabLogoUrlInput.trim() || null,
          logoScale: Number(logoScaleInput || 1),
          tabLogoScale: Number(tabLogoScaleInput || 1.12),
        },
      }));

      await cargarOpticas();
      cerrarModalLogo();
      Swal.fire({ icon: 'success', title: 'Logo actualizado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo guardar el logo' });
    } finally {
      setSaving(false);
    }
  };

  const guardarEscalaLogo = async () => {
    if (!logoModalOptica) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}`, {
        method: 'PUT',
        body: JSON.stringify({ logo_scale: Number(logoScaleInput || 1) }),
      });

      window.dispatchEvent(new CustomEvent('optica-logo-updated', {
        detail: {
          opticaId: logoModalOptica.id,
          logoUrl: logoModalOptica.logo_url || null,
          tabLogoUrl: logoModalOptica.logo_tab_url || logoModalOptica.tab_logo_url || null,
          logoScale: Number(logoScaleInput || 1),
          tabLogoScale: Number(tabLogoScaleInput || 1.12),
        },
      }));

      await cargarOpticas();
      Swal.fire({
        icon: 'success',
        title: 'Tamaño guardado',
        timer: 1100,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo guardar el tamaño del logo' });
    } finally {
      setSaving(false);
    }
  };

  const subirLogoOptica = async () => {
    if (!logoModalOptica) return;
    if (!selectedLogoFile) {
      Swal.fire({ icon: 'warning', title: 'Sin archivo', text: 'Seleccioná una imagen antes de subir' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('logo', selectedLogoFile);

      const response = await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}/logo-upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          logo_scale: Number(logoScaleInput || 1),
          logo_tab_url: tabLogoUrlInput.trim() || null,
          logo_tab_scale: Number(tabLogoScaleInput || 1.12),
        }),
      });
      window.dispatchEvent(new CustomEvent('optica-logo-updated', {
        detail: {
          opticaId: logoModalOptica.id,
          logoUrl: data.logo_url,
          tabLogoUrl: tabLogoUrlInput.trim() || logoModalOptica.logo_tab_url || logoModalOptica.tab_logo_url || null,
          logoScale: Number(logoScaleInput || 1),
          tabLogoScale: Number(tabLogoScaleInput || 1.12),
        },
      }));

      await cargarOpticas();
      cerrarModalLogo();
      Swal.fire({ icon: 'success', title: 'Logo actualizado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo subir el logo' });
    } finally {
      setSaving(false);
    }
  };

  const subirLogoTabOptica = async () => {
    if (!logoModalOptica) return;
    if (!selectedTabLogoFile) {
      Swal.fire({ icon: 'warning', title: 'Sin archivo', text: 'Seleccioná una imagen para el logo de pestaña' });
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('logo', selectedTabLogoFile);

      const response = await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}/logo-tab-upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setTabLogoUrlInput(data.logo_tab_url || '');

      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          logo_tab_scale: Number(tabLogoScaleInput || 1.12),
        }),
      });

      window.dispatchEvent(new CustomEvent('optica-logo-updated', {
        detail: {
          opticaId: logoModalOptica.id,
          logoUrl: logoUrlInput.trim() || logoModalOptica.logo_url || null,
          tabLogoUrl: data.logo_tab_url,
          logoScale: Number(logoScaleInput || 1),
          tabLogoScale: Number(tabLogoScaleInput || 1.12),
        },
      }));

      await cargarOpticas();
      setSelectedTabLogoFile(null);
      Swal.fire({ icon: 'success', title: 'Logo de pestaña actualizado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo subir el logo de pestaña' });
    } finally {
      setSaving(false);
    }
  };

  const guardarEscalaLogoTab = async () => {
    if (!logoModalOptica) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${logoModalOptica.id}`, {
        method: 'PUT',
        body: JSON.stringify({ logo_tab_scale: Number(tabLogoScaleInput || 1.12) }),
      });

      window.dispatchEvent(new CustomEvent('optica-logo-updated', {
        detail: {
          opticaId: logoModalOptica.id,
          logoUrl: logoModalOptica.logo_url || null,
          tabLogoUrl: tabLogoUrlInput.trim() || logoModalOptica.logo_tab_url || logoModalOptica.tab_logo_url || null,
          logoScale: Number(logoScaleInput || 1),
          tabLogoScale: Number(tabLogoScaleInput || 1.12),
        },
      }));

      await cargarOpticas();
      Swal.fire({
        icon: 'success',
        title: 'Tamaño pestaña guardado',
        timer: 1100,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo guardar el tamaño del logo de pestaña' });
    } finally {
      setSaving(false);
    }
  };

  const crearAdminOptica = async (e) => {
    e.preventDefault();
    const optica_id = Number(formAdmin.optica_id);
    const payload = {
      email: formAdmin.email.trim(),
      nombre_completo: formAdmin.nombre_completo.trim(),
      contraseña: formAdmin.contraseña,
    };

    if (!optica_id || !payload.email || !payload.nombre_completo || !payload.contraseña) {
      Swal.fire({ icon: 'warning', title: 'Datos incompletos', text: 'Completá todos los campos para crear el admin' });
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica_id}/admin`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setFormAdmin({ optica_id: '', email: '', nombre_completo: '', contraseña: '' });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({ icon: 'success', title: 'Admin creado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo crear el admin' });
    } finally {
      setSaving(false);
    }
  };

  const obtenerPlanLabel = (planCodigo) => {
    const encontrado = planes.find((p) => p.codigo === planCodigo);
    return encontrado?.nombre || planCodigo;
  };

  const obtenerModuloLabel = (moduloCodigo) => {
    const encontrado = modulosCatalogo.find((m) => m.codigo === moduloCodigo);
    return encontrado?.nombre || moduloCodigo;
  };

  const actualizarPlanOptica = async (optica) => {
    const planOptions = planes
      .map((p) => `<option value="${p.codigo}" ${p.codigo === optica.plan_codigo ? 'selected' : ''}>${p.nombre} (${p.max_usuarios} usuarios)</option>`)
      .join('');

    const { value: formValues } = await Swal.fire({
      title: `Plan de ${optica.nombre}`,
      html:
        `<label style="display:block;text-align:left;margin:0 0 6px 2px;font-size:13px;">Plan</label>` +
        `<select id="swal-plan" class="swal2-input">${planOptions}</select>` +
        `<label style="display:block;text-align:left;margin:8px 0 6px 2px;font-size:13px;">Override de cupo (opcional)</label>` +
        `<input id="swal-override" type="number" min="1" class="swal2-input" placeholder="Ej: 10" value="${optica.max_usuarios_override || ''}">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar plan',
      preConfirm: () => {
        const plan_codigo = document.getElementById('swal-plan')?.value;
        const overrideRaw = document.getElementById('swal-override')?.value;
        const overrideTrim = (overrideRaw || '').trim();
        if (!plan_codigo) {
          Swal.showValidationMessage('Debés seleccionar un plan');
          return null;
        }
        if (overrideTrim && Number(overrideTrim) < 1) {
          Swal.showValidationMessage('El override debe ser mayor o igual a 1');
          return null;
        }
        return {
          plan_codigo,
          max_usuarios_override: overrideTrim ? Number(overrideTrim) : null,
        };
      },
    });

    if (!formValues) {
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}/plan`, {
        method: 'PUT',
        body: JSON.stringify(formValues),
      });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({ icon: 'success', title: 'Plan actualizado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo actualizar el plan' });
    } finally {
      setSaving(false);
    }
  };

  const actualizarModulosOptica = async (optica) => {
    const modulosActivos = new Set(Array.isArray(optica.modulos_habilitados) ? optica.modulos_habilitados : []);
    const usandoDefaultPlan = optica.modulos_override == null;

    const modulosHtml = modulosCatalogo.length > 0
      ? modulosCatalogo
        .map(
          (m) =>
            `<label style="display:flex;align-items:center;gap:8px;margin:6px 0;font-size:14px;">` +
            `<input type="checkbox" name="swal-modulo" value="${m.codigo}" ${modulosActivos.has(m.codigo) ? 'checked' : ''}>` +
            `<span>${m.nombre} <small style="color:#64748b">(${m.grupo})</small></span>` +
            `</label>`
        )
        .join('')
      : '<p style="font-size:13px;color:#64748b;">No hay módulos disponibles.</p>';

    const { value: formValues } = await Swal.fire({
      title: `Módulos de ${optica.nombre}`,
      html:
        `<div style="text-align:left;margin-bottom:10px;">` +
        `<label style="display:flex;align-items:center;gap:8px;font-size:14px;">` +
        `<input id="swal-modulos-plan-default" type="checkbox" ${usandoDefaultPlan ? 'checked' : ''}>` +
        `<span>Usar módulos por plan (${obtenerPlanLabel(optica.plan_codigo)})</span>` +
        `</label>` +
        `<p style="margin:6px 0 0 26px;font-size:12px;color:#64748b;">Si se activa, ignora personalizaciones y aplica el plan base.</p>` +
        `</div>` +
        `<div style="max-height:280px;overflow:auto;border:1px solid #e2e8f0;border-radius:8px;padding:8px 10px;">${modulosHtml}</div>`,
      width: 640,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar módulos',
      preConfirm: () => {
        const usarPlanDefault = !!document.getElementById('swal-modulos-plan-default')?.checked;
        if (usarPlanDefault) {
          return { modulos_override: null };
        }

        const seleccionados = Array.from(
          document.querySelectorAll('input[name="swal-modulo"]:checked')
        ).map((el) => el.value);

        if (seleccionados.length === 0) {
          Swal.showValidationMessage('Seleccioná al menos un módulo o activá "Usar módulos por plan".');
          return null;
        }

        return { modulos_override: seleccionados };
      },
    });

    if (!formValues) return;

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${optica.id}/modulos`, {
        method: 'PUT',
        body: JSON.stringify(formValues),
      });
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarAuditoria()]);
      Swal.fire({ icon: 'success', title: 'Módulos actualizados', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudieron actualizar los módulos' });
    } finally {
      setSaving(false);
    }
  };

  const cargarUsuariosOptica = async (opticaId) => {
    if (!opticaId) {
      setUsuariosOptica([]);
      return;
    }

    try {
      setLoadingUsuarios(true);
      const response = await fetchConAuth(`${API_URL}/api/superadmin/opticas/${opticaId}/usuarios`);
      const data = await response.json();
      setUsuariosOptica(data || []);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudieron cargar los usuarios' });
    } finally {
      setLoadingUsuarios(false);
    }
  };

  const onChangeOpticaGestionUsuarios = async (value) => {
    setOpticaGestionUsuariosId(value);
    setFormUsuarioOptica(initialFormUsuarioOptica);
    await cargarUsuariosOptica(value);
  };

  const crearUsuarioOptica = async (e) => {
    e.preventDefault();
    const opticaId = Number(opticaGestionUsuariosId);
    const payload = {
      email: formUsuarioOptica.email.trim(),
      nombre_completo: formUsuarioOptica.nombre_completo.trim(),
      contraseña: formUsuarioOptica.contraseña,
      rol: formUsuarioOptica.rol,
    };

    if (!opticaId || !payload.email || !payload.nombre_completo || !payload.contraseña) {
      Swal.fire({ icon: 'warning', title: 'Datos incompletos', text: 'Completá todos los campos para crear usuario' });
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${opticaId}/usuarios`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      setFormUsuarioOptica(initialFormUsuarioOptica);
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarUsuariosOptica(opticaId)]);
      Swal.fire({ icon: 'success', title: 'Usuario creado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo crear el usuario' });
    } finally {
      setSaving(false);
    }
  };

  const editarUsuarioOptica = async (usr) => {
    const { value: formValues } = await Swal.fire({
      title: `Editar usuario #${usr.id}`,
      html:
        `<input id="swal-nombre" class="swal2-input" placeholder="Nombre" value="${usr.nombre_completo || ''}">` +
        `<input id="swal-email" class="swal2-input" placeholder="Email" value="${usr.email || ''}">` +
        `<input id="swal-password" class="swal2-input" placeholder="Nueva contraseña (opcional)" type="password">`,
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const nombre_completo = document.getElementById('swal-nombre')?.value?.trim();
        const email = document.getElementById('swal-email')?.value?.trim();
        const contraseña = document.getElementById('swal-password')?.value || '';
        if (!nombre_completo || !email) {
          Swal.showValidationMessage('Nombre y email son obligatorios');
          return null;
        }
        return {
          nombre_completo,
          email,
          ...(contraseña ? { contraseña } : {}),
        };
      },
    });

    if (!formValues) {
      return;
    }

    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${usr.optica_id}/usuarios/${usr.id}`, {
        method: 'PUT',
        body: JSON.stringify(formValues),
      });
      await Promise.all([cargarOpticas(), cargarUsuariosOptica(String(usr.optica_id))]);
      Swal.fire({ icon: 'success', title: 'Usuario actualizado', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo actualizar el usuario' });
    } finally {
      setSaving(false);
    }
  };

  const cambiarRolUsuarioOptica = async (usr, nuevoRol) => {
    try {
      setSaving(true);
      await fetchConAuth(`${API_URL}/api/superadmin/opticas/${usr.optica_id}/usuarios/${usr.id}`, {
        method: 'PUT',
        body: JSON.stringify({ rol: nuevoRol }),
      });
      await Promise.all([cargarOpticas(), cargarUsuariosOptica(String(usr.optica_id))]);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo cambiar el rol' });
    } finally {
      setSaving(false);
    }
  };

  const toggleEstadoUsuarioOptica = async (usr) => {
    try {
      setSaving(true);
      if (usr.activo) {
        await fetchConAuth(`${API_URL}/api/superadmin/opticas/${usr.optica_id}/usuarios/${usr.id}`, {
          method: 'DELETE',
        });
      } else {
        await fetchConAuth(`${API_URL}/api/superadmin/opticas/${usr.optica_id}/usuarios/${usr.id}/activar`, {
          method: 'PUT',
        });
      }
      await Promise.all([cargarMetricas(), cargarOpticas(), cargarUsuariosOptica(String(usr.optica_id))]);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Error', text: e.message || 'No se pudo cambiar el estado del usuario' });
    } finally {
      setSaving(false);
    }
  };

  const opticaSeleccionadaUsuarios = useMemo(
    () => opticasData.items.find((o) => String(o.id) === String(opticaGestionUsuariosId)),
    [opticasData.items, opticaGestionUsuariosId]
  );

  const irPaginaOpticas = async (newPage) => {
    const next = { ...filtroOpticas, page: newPage };
    setFiltroOpticas(next);
    await cargarOpticas(next);
  };

  const irPaginaAuditoria = async (newPage) => {
    const next = { ...filtroAuditoria, page: newPage };
    setFiltroAuditoria(next);
    await cargarAuditoria(next);
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Cargando panel superadmin...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
        <p className="text-sm text-gray-600">Gestión completa de ópticas, admins y auditoría.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Ópticas Totales</p><p className="text-2xl font-bold">{metricas?.total_opticas ?? 0}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Ópticas Activas</p><p className="text-2xl font-bold text-emerald-600">{metricas?.opticas_activas ?? 0}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Ópticas Inactivas</p><p className="text-2xl font-bold text-rose-600">{metricas?.opticas_inactivas ?? 0}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Usuarios Totales</p><p className="text-2xl font-bold">{metricas?.total_usuarios ?? 0}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Admins</p><p className="text-2xl font-bold">{metricas?.total_admins ?? 0}</p></div>
        <div className="bg-white rounded-lg shadow p-4"><p className="text-xs text-gray-500">Superadmins</p><p className="text-2xl font-bold">{metricas?.total_superadmins ?? 0}</p></div>
      </div>

      <div className="bg-white rounded-lg shadow p-2 flex gap-2 flex-wrap w-fit">
        <button onClick={() => setTab('opticas')} className={`px-4 py-2 rounded font-medium transition-colors ${tab === 'opticas' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-slate-700 hover:bg-gray-200'}`}>Ópticas</button>
        <button onClick={() => setTab('cobranzas')} className={`px-4 py-2 rounded font-medium transition-colors ${tab === 'cobranzas' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-slate-700 hover:bg-gray-200'}`}>Cobranzas y Pagos</button>
        <button onClick={() => setTab('auditoria')} className={`px-4 py-2 rounded font-medium transition-colors ${tab === 'auditoria' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-slate-700 hover:bg-gray-200'}`}>Auditoría</button>
      </div>

      {tab === 'opticas' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
              <h2 className="font-semibold">Matriz de planes y módulos</h2>
              <p className="text-sm text-gray-600">
                Esta matriz muestra la base por plan. Después podés personalizar cualquier óptica desde el botón "Módulos".
              </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={revertirMatriz}
                  disabled={saving || !matrizDirty}
                  className="px-3 py-2 text-sm rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50"
                >
                  Revertir
                </button>
                <button
                  type="button"
                  onClick={guardarMatrizPlanes}
                  disabled={saving || !matrizDirty}
                  className="px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Guardar matriz
                </button>
              </div>
            </div>

            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm min-w-[760px]">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2">Módulo</th>
                    <th className="text-left px-3 py-2">Grupo</th>
                    {planesOrdenados.map((plan) => (
                      <th key={plan.codigo} className="text-center px-3 py-2">
                        <div className="font-semibold">{plan.nombre}</div>
                        <div className="text-[11px] text-gray-500">{plan.max_usuarios} usuarios</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modulosOrdenados.map((modulo) => (
                    <tr key={modulo.codigo} className="border-t">
                      <td className="px-3 py-2 font-medium text-gray-800">{modulo.nombre}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 text-xs rounded font-medium ${modulo.grupo === 'ecommerce' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-slate-100 text-slate-700'}`}>
                          {modulo.grupo}
                        </span>
                      </td>
                      {planesOrdenados.map((plan) => {
                        const habilitado = moduloIncluidoEnPlan(modulo.codigo, plan.codigo);
                        return (
                          <td key={`${modulo.codigo}-${plan.codigo}`} className="px-3 py-2 text-center">
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={habilitado}
                                onChange={() => toggleModuloMatriz(plan.codigo, modulo.codigo)}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className={`text-xs font-medium ${habilitado ? 'text-emerald-700' : 'text-rose-700'}`}>
                                {habilitado ? 'Sí' : 'No'}
                              </span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {modulosOrdenados.length === 0 && (
                    <tr>
                      <td colSpan={2 + planesOrdenados.length} className="px-3 py-6 text-center text-gray-500">
                        No hay módulos configurados para mostrar la matriz.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold mb-3">Crear nueva óptica</h2>
            <form className="grid md:grid-cols-3 gap-3" onSubmit={crearOptica}>
              <input className="border rounded px-3 py-2" placeholder="Nombre" value={formOptica.nombre} onChange={(e) => setFormOptica((p) => ({ ...p, nombre: e.target.value }))} />
              <input className="border rounded px-3 py-2" placeholder="Slug" value={formOptica.slug} onChange={(e) => setFormOptica((p) => ({ ...p, slug: e.target.value }))} />
              <button disabled={saving} className="bg-emerald-600 text-white rounded px-3 py-2 hover:bg-emerald-700 disabled:opacity-60">Crear</button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="font-semibold mb-3">Crear admin por óptica</h2>
            <form className="grid md:grid-cols-4 gap-3" onSubmit={crearAdminOptica}>
              <select className="border rounded px-3 py-2" value={formAdmin.optica_id} onChange={(e) => setFormAdmin((p) => ({ ...p, optica_id: e.target.value }))}>
                <option value="">Seleccionar óptica</option>
                {opticasData.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.nombre} #{o.id}</option>
                ))}
              </select>
              <input className="border rounded px-3 py-2" placeholder="Nombre completo" value={formAdmin.nombre_completo} onChange={(e) => setFormAdmin((p) => ({ ...p, nombre_completo: e.target.value }))} />
              <input className="border rounded px-3 py-2" placeholder="Email" value={formAdmin.email} onChange={(e) => setFormAdmin((p) => ({ ...p, email: e.target.value }))} />
              <input type="password" className="border rounded px-3 py-2" placeholder="Contraseña" value={formAdmin.contraseña} onChange={(e) => setFormAdmin((p) => ({ ...p, contraseña: e.target.value }))} />
              <button disabled={saving} className="md:col-span-4 bg-indigo-600 text-white rounded px-3 py-2 hover:bg-indigo-700 disabled:opacity-60">Crear admin</button>
            </form>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="grid md:grid-cols-4 gap-3">
              <input
                className="border rounded px-3 py-2"
                placeholder="Buscar por nombre o slug"
                value={filtroOpticas.busqueda}
                onChange={(e) => setFiltroOpticas((p) => ({ ...p, busqueda: e.target.value, page: 1 }))}
              />
              <select
                className="border rounded px-3 py-2"
                value={filtroOpticas.activo}
                onChange={(e) => setFiltroOpticas((p) => ({ ...p, activo: e.target.value, page: 1 }))}
              >
                <option value="todos">Todos</option>
                <option value="activo">Activas</option>
                <option value="inactivo">Inactivas</option>
              </select>
              <select
                className="border rounded px-3 py-2"
                value={filtroOpticas.page_size}
                onChange={(e) => setFiltroOpticas((p) => ({ ...p, page_size: Number(e.target.value), page: 1 }))}
              >
                <option value={10}>10 por página</option>
                <option value={20}>20 por página</option>
                <option value={50}>50 por página</option>
              </select>
              <button
                onClick={() => cargarOpticas(filtroOpticas)}
                className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700"
              >
                Aplicar filtros
              </button>
            </div>

            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2">ID</th>
                    <th className="text-left px-3 py-2">Nombre</th>
                    <th className="text-left px-3 py-2">Alta / Antigüedad</th>
                    <th className="text-left px-3 py-2">Plan</th>
                    <th className="text-left px-3 py-2">Suscripción / Prueba</th>
                    <th className="text-left px-3 py-2">Módulos</th>
                    <th className="text-left px-3 py-2">Cupo</th>
                    <th className="text-left px-3 py-2">Estado</th>
                    <th className="text-left px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {opticasData.items.map((o) => (
                    <tr key={o.id} className="border-t hover:bg-slate-50/70 transition-colors">
                      <td className="px-3 py-2 text-slate-500 font-mono text-xs">{o.id}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900">{o.nombre}</div>
                        <div className="text-xs text-slate-400 font-mono">{o.slug}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800 text-xs">{formatearFecha(o.created_at)}</div>
                        <div className="text-[11px] text-slate-500">{calcularAntiguedad(o.created_at)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 text-xs rounded font-medium bg-indigo-100 text-indigo-700">{obtenerPlanLabel(o.plan_codigo)}</span>
                      </td>
                      <td className="px-3 py-2">
                        {o.es_trial ? (
                          (() => {
                            const dias = calcularDiasRestantes(o.trial_vence_at);
                            if (dias === null) {
                              return <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 font-medium">Trial</span>;
                            }
                            if (dias <= 0) {
                              return (
                                <div>
                                  <span className="px-2 py-0.5 text-xs rounded bg-rose-100 text-rose-800 font-bold border border-rose-200">
                                    Trial Vencido ({Math.abs(dias)}d atrás)
                                  </span>
                                  <div className="text-[11px] text-rose-600 font-semibold mt-0.5">Acceso bloqueado</div>
                                </div>
                              );
                            }
                            if (dias <= 7) {
                              return (
                                <div>
                                  <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-900 font-bold border border-amber-300 animate-pulse">
                                    Vence en {dias} {dias === 1 ? 'día' : 'días'}
                                  </span>
                                  <div className="text-[11px] text-slate-500 mt-0.5">{formatearFecha(o.trial_vence_at)}</div>
                                </div>
                              );
                            }
                            return (
                              <div>
                                <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 font-medium border border-blue-200">
                                  Prueba Activa ({dias} días)
                                </span>
                                <div className="text-[11px] text-slate-500 mt-0.5">Vence: {formatearFecha(o.trial_vence_at)}</div>
                              </div>
                            );
                          })()
                        ) : (
                          <div>
                            <span className="px-2 py-0.5 text-xs rounded bg-emerald-100 text-emerald-800 font-semibold border border-emerald-200">
                              Plan Comercial
                            </span>
                            {o.proximo_vencimiento_pago && (
                              <div className="text-[11px] text-slate-500 mt-0.5">
                                Vence: {formatearFecha(o.proximo_vencimiento_pago)}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700" title={(o.modulos_habilitados || []).map(obtenerModuloLabel).join(', ')}>
                            {(o.modulos_habilitados || []).length} mod.
                          </span>
                          {o.modulos_override == null ? (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-emerald-100 text-emerald-700">plan</span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-700">custom</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{o.usuarios_activos}/{o.max_usuarios_permitidos}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 text-xs rounded font-medium ${o.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                          {o.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {o.es_trial && (
                            <>
                              <button
                                disabled={saving}
                                onClick={() => habilitarPlanDefinitivo(o)}
                                title="Habilita plan comercial permanente y quita corte de trial"
                                className="px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm transition-all"
                              >
                                Habilitar Plan
                              </button>
                              <button
                                disabled={saving}
                                onClick={() => extenderTrial(o, 30)}
                                title="Extiende 30 días adicionales el período de prueba"
                                className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 hover:bg-blue-200 transition-all"
                              >
                                +30d Trial
                              </button>
                            </>
                          )}
                          <button
                            disabled={saving}
                            onClick={() => abrirModalRegistrarPago(o)}
                            title="Registrar pago recibido de esta óptica"
                            className="px-2 py-1 text-xs font-medium rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-all border border-indigo-200"
                          >
                            Cobro
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => editarOptica(o)}
                            className="px-2 py-1 text-xs rounded bg-slate-200 text-slate-800 hover:bg-slate-300"
                          >
                            Editar
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => actualizarPlanOptica(o)}
                            className="px-2 py-1 text-xs rounded bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                          >
                            Plan
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => actualizarModulosOptica(o)}
                            className="px-2 py-1 text-xs rounded bg-cyan-100 text-cyan-800 hover:bg-cyan-200"
                          >
                            Módulos
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => abrirModalLogo(o)}
                            className="px-2 py-1 text-xs rounded bg-violet-100 text-violet-800 hover:bg-violet-200"
                          >
                            Logo
                          </button>
                          <button
                            disabled={saving}
                            onClick={() => toggleEstadoOptica(o)}
                            className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-800 hover:bg-amber-200"
                          >
                            {o.activo ? 'Desactivar' : 'Activar'}
                          </button>
                          {o.id !== 1 && (
                            <button
                              disabled={saving}
                              onClick={() => eliminarOptica(o)}
                              title="Eliminar óptica definitivamente"
                              className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700 hover:bg-red-200 border border-red-200 transition-colors"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">Total: {opticasData.total}</p>
              <div className="flex items-center gap-2">
                <button disabled={opticasData.page <= 1} onClick={() => irPaginaOpticas(opticasData.page - 1)} className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50">Anterior</button>
                <span className="text-sm">Página {opticasData.page} de {opticasData.total_pages || 1}</span>
                <button disabled={opticasData.page >= (opticasData.total_pages || 1)} onClick={() => irPaginaOpticas(opticasData.page + 1)} className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50">Siguiente</button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Gestión de usuarios por óptica</h2>
              {opticaSeleccionadaUsuarios && (
                <span className="text-sm text-gray-600">
                  Cupo activo: {opticaSeleccionadaUsuarios.usuarios_activos}/{opticaSeleccionadaUsuarios.max_usuarios_permitidos}
                </span>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              <select
                className="border rounded px-3 py-2"
                value={opticaGestionUsuariosId}
                onChange={(e) => onChangeOpticaGestionUsuarios(e.target.value)}
              >
                <option value="">Seleccionar óptica</option>
                {opticasData.items.map((o) => (
                  <option key={o.id} value={o.id}>{o.nombre} #{o.id}</option>
                ))}
              </select>
              <button
                className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700"
                onClick={() => cargarUsuariosOptica(opticaGestionUsuariosId)}
                disabled={!opticaGestionUsuariosId || loadingUsuarios}
              >
                Recargar usuarios
              </button>
            </div>

            {opticaGestionUsuariosId && (
              <>
                <form className="grid md:grid-cols-5 gap-3" onSubmit={crearUsuarioOptica}>
                  <input
                    className="border rounded px-3 py-2"
                    placeholder="Nombre completo"
                    value={formUsuarioOptica.nombre_completo}
                    onChange={(e) => setFormUsuarioOptica((p) => ({ ...p, nombre_completo: e.target.value }))}
                  />
                  <input
                    className="border rounded px-3 py-2"
                    placeholder="Email"
                    value={formUsuarioOptica.email}
                    onChange={(e) => setFormUsuarioOptica((p) => ({ ...p, email: e.target.value }))}
                  />
                  <input
                    type="password"
                    className="border rounded px-3 py-2"
                    placeholder="Contraseña"
                    value={formUsuarioOptica.contraseña}
                    onChange={(e) => setFormUsuarioOptica((p) => ({ ...p, contraseña: e.target.value }))}
                  />
                  <select
                    className="border rounded px-3 py-2"
                    value={formUsuarioOptica.rol}
                    onChange={(e) => setFormUsuarioOptica((p) => ({ ...p, rol: e.target.value }))}
                  >
                    <option value="operario">Operario</option>
                    <option value="gerente">Gerente</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button disabled={saving} className="bg-emerald-600 text-white rounded px-3 py-2 hover:bg-emerald-700 disabled:opacity-60">
                    Crear usuario
                  </button>
                </form>

                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">Nombre</th>
                        <th className="text-left px-3 py-2">Email</th>
                        <th className="text-left px-3 py-2">Rol</th>
                        <th className="text-left px-3 py-2">Estado</th>
                        <th className="text-left px-3 py-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosOptica.map((usr) => (
                        <tr key={usr.id} className="border-t">
                          <td className="px-3 py-2">{usr.id}</td>
                          <td className="px-3 py-2">{usr.nombre_completo}</td>
                          <td className="px-3 py-2">{usr.email}</td>
                          <td className="px-3 py-2">
                            <select
                              className="border rounded px-2 py-1"
                              value={usr.rol}
                              onChange={(e) => cambiarRolUsuarioOptica(usr, e.target.value)}
                              disabled={saving}
                            >
                              <option value="operario">Operario</option>
                              <option value="gerente">Gerente</option>
                              <option value="admin">Admin</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-1 text-xs rounded ${usr.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                              {usr.activo ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <button
                              disabled={saving}
                              onClick={() => editarUsuarioOptica(usr)}
                              className="px-2 py-1 rounded bg-slate-200 text-slate-800 mr-2"
                            >
                              Editar
                            </button>
                            <button
                              disabled={saving}
                              onClick={() => toggleEstadoUsuarioOptica(usr)}
                              className={`px-2 py-1 rounded ${usr.activo ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}
                            >
                              {usr.activo ? 'Desactivar' : 'Activar'}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!loadingUsuarios && usuariosOptica.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-gray-500">No hay usuarios para esta óptica</td>
                        </tr>
                      )}
                      {loadingUsuarios && (
                        <tr>
                          <td colSpan={6} className="px-3 py-6 text-center text-gray-500">Cargando usuarios...</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'cobranzas' && (
        <div className="space-y-6">
          {/* Métricas de Cobranzas y Planes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5 border border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recaudado este Mes</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">
                ${(pagosData?.metricas?.total_cobrado_mes || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">Suscripciones abonadas</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border border-slate-100">
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Ópticas Al Día</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1">
                {pagosData?.metricas?.opticas_al_dia ?? 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">Planes comerciales activos</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border border-slate-100">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">En Período de Prueba</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {pagosData?.metricas?.opticas_en_trial ?? 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">Trial de 3 meses activo</p>
            </div>

            <div className="bg-white rounded-xl shadow p-5 border border-slate-100">
              <p className="text-xs font-semibold text-rose-700 uppercase tracking-wide">Vencidas / En Riesgo</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">
                {pagosData?.metricas?.opticas_vencidas ?? 0}
              </p>
              <p className="text-[11px] text-slate-400 mt-0.5">Prueba o servicio expirado</p>
            </div>
          </div>

          {/* Barra de Filtros y Acción de Registro */}
          <div className="bg-white rounded-xl shadow p-4 space-y-4 border border-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  className="border rounded-lg px-3 py-2 text-sm bg-slate-50 focus:bg-white outline-none min-w-[220px]"
                  value={filtroPagoOpticaId}
                  onChange={(e) => setFiltroPagoOpticaId(e.target.value)}
                >
                  <option value="">Todas las ópticas</option>
                  {opticasData.items.map((o) => (
                    <option key={o.id} value={o.id}>{o.nombre} (#{o.id})</option>
                  ))}
                </select>

                <button
                  onClick={() => cargarPagos(1, filtroPagoOpticaId)}
                  disabled={loadingPagos}
                  className="px-4 py-2 text-sm rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                >
                  {loadingPagos ? 'Cargando...' : 'Actualizar'}
                </button>
              </div>

              <button
                onClick={() => abrirModalRegistrarPago()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-md shadow-emerald-600/20 transition-all cursor-pointer"
              >
                + Registrar Pago de Óptica
              </button>
            </div>

            {/* Tabla de Pagos Registrados */}
            <div className="overflow-x-auto border rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 border-b">
                  <tr>
                    <th className="text-left px-3 py-2.5">ID</th>
                    <th className="text-left px-3 py-2.5">Fecha</th>
                    <th className="text-left px-3 py-2.5">Óptica</th>
                    <th className="text-left px-3 py-2.5">Plan</th>
                    <th className="text-left px-3 py-2.5">Período</th>
                    <th className="text-right px-3 py-2.5">Monto</th>
                    <th className="text-left px-3 py-2.5">Método</th>
                    <th className="text-left px-3 py-2.5">Próx. Vencimiento</th>
                    <th className="text-left px-3 py-2.5">Comprobante / Notas</th>
                    <th className="text-center px-3 py-2.5">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pagosData.items.map((pago) => (
                    <tr key={pago.id} className="border-t hover:bg-slate-50/70 transition-colors">
                      <td className="px-3 py-2.5 text-xs font-mono text-slate-400">#{pago.id}</td>
                      <td className="px-3 py-2.5 font-medium text-slate-800 text-xs">
                        {formatearFecha(pago.fecha_pago)}
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-slate-900">
                        {pago.optica_nombre}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-50 text-indigo-700 font-medium border border-indigo-200">
                          {obtenerPlanLabel(pago.plan_codigo)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 font-medium text-slate-700">
                        {pago.periodo}
                      </td>
                      <td className="px-3 py-2.5 text-right font-bold text-slate-900 font-mono">
                        ${pago.monto.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="capitalize px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-700">
                          {pago.metodo_pago}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600">
                        {formatearFecha(pago.fecha_proximo_vencimiento)}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500 max-w-xs truncate" title={pago.notas || pago.comprobante_nro || ''}>
                        {pago.comprobante_nro && <span className="font-mono text-slate-700 mr-1.5 font-medium">[{pago.comprobante_nro}]</span>}
                        {pago.notas || '-'}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <button
                          onClick={() => eliminarPago(pago)}
                          disabled={saving}
                          title="Eliminar este cobro"
                          className="px-2 py-1 text-xs text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loadingPagos && pagosData.items.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                        No hay pagos registrados aún. Hacé clic en "+ Registrar Pago de Óptica" para asentar el primer cobro.
                      </td>
                    </tr>
                  )}
                  {loadingPagos && (
                    <tr>
                      <td colSpan={10} className="px-3 py-8 text-center text-slate-500">
                        Cargando listado de pagos...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginador de Pagos */}
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-slate-500">Total registrados: {pagosData.total}</p>
              <div className="flex items-center gap-2">
                <button
                  disabled={pagosData.page <= 1 || loadingPagos}
                  onClick={() => cargarPagos(pagosData.page - 1, filtroPagoOpticaId)}
                  className="px-3 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 text-sm"
                >
                  Anterior
                </button>
                <span className="text-sm text-slate-600">Página {pagosData.page}</span>
                <button
                  disabled={pagosData.items.length < 20 || loadingPagos}
                  onClick={() => cargarPagos(pagosData.page + 1, filtroPagoOpticaId)}
                  className="px-3 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 text-sm"
                >
                  Siguiente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'auditoria' && (
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <div className="grid md:grid-cols-5 gap-3">
            <input
              className="border rounded px-3 py-2"
              placeholder="Buscar en auditoría"
              value={filtroAuditoria.busqueda}
              onChange={(e) => setFiltroAuditoria((p) => ({ ...p, busqueda: e.target.value, page: 1 }))}
            />
            <select
              className="border rounded px-3 py-2"
              value={filtroAuditoria.accion}
              onChange={(e) => setFiltroAuditoria((p) => ({ ...p, accion: e.target.value, page: 1 }))}
            >
              <option value="">Todas las acciones</option>
              {accionesDisponibles.map((a) => (<option key={a} value={a}>{a}</option>))}
            </select>
            <select
              className="border rounded px-3 py-2"
              value={filtroAuditoria.optica_id}
              onChange={(e) => setFiltroAuditoria((p) => ({ ...p, optica_id: e.target.value, page: 1 }))}
            >
              <option value="">Todas las ópticas</option>
              {opticasData.items.map((o) => (<option key={o.id} value={o.id}>{o.nombre}</option>))}
            </select>
            <select
              className="border rounded px-3 py-2"
              value={filtroAuditoria.page_size}
              onChange={(e) => setFiltroAuditoria((p) => ({ ...p, page_size: Number(e.target.value), page: 1 }))}
            >
              <option value={20}>20 por página</option>
              <option value={50}>50 por página</option>
              <option value={100}>100 por página</option>
            </select>
            <button onClick={() => cargarAuditoria(filtroAuditoria)} className="bg-blue-600 text-white rounded px-3 py-2 hover:bg-blue-700">Aplicar filtros</button>
          </div>

          <div className="overflow-x-auto border rounded">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Acción</th>
                  <th className="text-left px-3 py-2">Óptica</th>
                  <th className="text-left px-3 py-2">Actor</th>
                  <th className="text-left px-3 py-2">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {auditoriaData.items.map((item) => (
                  <tr key={item.id} className="border-t align-top">
                    <td className="px-3 py-2">{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                    <td className="px-3 py-2"><span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs">{item.accion}</span></td>
                    <td className="px-3 py-2">{item.optica_nombre || `#${item.optica_id || '-'}`}</td>
                    <td className="px-3 py-2">{item.actor_nombre}<div className="text-xs text-gray-500">{item.actor_email}</div></td>
                    <td className="px-3 py-2 text-gray-700">{item.detalle || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">Total: {auditoriaData.total}</p>
            <div className="flex items-center gap-2">
              <button disabled={auditoriaData.page <= 1} onClick={() => irPaginaAuditoria(auditoriaData.page - 1)} className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50">Anterior</button>
              <span className="text-sm">Página {auditoriaData.page} de {auditoriaData.total_pages || 1}</span>
              <button disabled={auditoriaData.page >= (auditoriaData.total_pages || 1)} onClick={() => irPaginaAuditoria(auditoriaData.page + 1)} className="px-3 py-1 rounded bg-gray-100 disabled:opacity-50">Siguiente</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Pago de Óptica */}
      {modalRegistrarPago && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-[1050] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-auto overflow-hidden border border-slate-100">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-4 text-white flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Registrar Pago de Óptica</h3>
                <p className="text-xs text-emerald-100 mt-0.5">Asentar cobro y renovar período de suscripción</p>
              </div>
              <button
                type="button"
                onClick={() => setModalRegistrarPago(false)}
                className="text-white/80 hover:text-white text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <form onSubmit={registrarPago} className="p-6 space-y-4">
              {/* Selección de Óptica */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                  Óptica Receptora <span className="text-rose-500">*</span>
                </label>
                <select
                  required
                  className="w-full border rounded-xl px-3.5 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none"
                  value={formPago.optica_id}
                  onChange={(e) => {
                    const optId = e.target.value;
                    const optEncontrada = opticasData.items.find((o) => String(o.id) === optId);
                    setFormPago((prev) => ({
                      ...prev,
                      optica_id: optId,
                      plan_codigo: optEncontrada ? optEncontrada.plan_codigo : prev.plan_codigo,
                    }));
                  }}
                >
                  <option value="">Seleccionar óptica...</option>
                  {opticasData.items.map((o) => (
                    <option key={o.id} value={o.id}>{o.nombre} (#{o.id}) - Plan: {obtenerPlanLabel(o.plan_codigo)}</option>
                  ))}
                </select>
              </div>

              {/* Plan y Período */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    Plan Contratado <span className="text-rose-500">*</span>
                  </label>
                  <select
                    required
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none"
                    value={formPago.plan_codigo}
                    onChange={(e) => setFormPago((p) => ({ ...p, plan_codigo: e.target.value }))}
                  >
                    <option value="basico">Básico</option>
                    <option value="esferico">Esférico</option>
                    <option value="asferico_hd">Asférico HD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    Período Abonado <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ej: Noviembre 2026"
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none"
                    value={formPago.periodo}
                    onChange={(e) => setFormPago((p) => ({ ...p, periodo: e.target.value }))}
                  />
                </div>
              </div>

              {/* Monto y Método de Pago */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    Monto Abonado ($) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    placeholder="Ej: 45000"
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none font-semibold text-slate-800"
                    value={formPago.monto}
                    onChange={(e) => setFormPago((p) => ({ ...p, monto: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    Método de Pago <span className="text-rose-500">*</span>
                  </label>
                  <select
                    required
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none"
                    value={formPago.metodo_pago}
                    onChange={(e) => setFormPago((p) => ({ ...p, metodo_pago: e.target.value }))}
                  >
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="mercadopago">Mercado Pago</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
              </div>

              {/* Renovación de Suscripción */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                  Renovar Suscripción Por <span className="text-rose-500">*</span>
                </label>
                <select
                  className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none font-medium text-slate-800"
                  value={formPago.renovar_dias}
                  onChange={(e) => setFormPago((p) => ({ ...p, renovar_dias: Number(e.target.value) }))}
                >
                  <option value={30}>+ 30 días (1 mes de servicio)</option>
                  <option value={60}>+ 60 días (2 meses de servicio)</option>
                  <option value={90}>+ 90 días (3 meses / Trimestre)</option>
                  <option value={180}>+ 180 días (6 meses / Semestre)</option>
                  <option value={365}>+ 365 días (1 año / Plan Anual)</option>
                </select>
                <p className="text-[11px] text-slate-400 mt-1">Al asentar el pago, la óptica pasa a Plan Comercial Activo y se posterga su vencimiento.</p>
              </div>

              {/* Nro de Comprobante y Notas */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    N° Comprobante / Op. <span className="text-slate-400 font-normal lowercase">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Transf #9928341"
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none font-mono"
                    value={formPago.comprobante_nro}
                    onChange={(e) => setFormPago((p) => ({ ...p, comprobante_nro: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wide mb-1">
                    Notas Internas <span className="text-slate-400 font-normal lowercase">(opcional)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ej: Pagó con descuento promocional"
                    className="w-full border rounded-xl px-3 py-2.5 text-sm bg-slate-50 focus:bg-white outline-none"
                    value={formPago.notas}
                    onChange={(e) => setFormPago((p) => ({ ...p, notas: e.target.value }))}
                  />
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setModalRegistrarPago(false)}
                  disabled={saving}
                  className="px-4 py-2.5 text-sm text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-md shadow-emerald-600/25 transition-all disabled:opacity-50 cursor-pointer"
                >
                  {saving ? 'Registrando...' : 'Asentar Cobro y Renovar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {logoModalOptica && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[1300] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md relative overflow-hidden">
            {saving && (
              <div className="absolute inset-0 z-20 bg-white/75 backdrop-blur-[1px] flex items-center justify-center">
                <div className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-4 py-2 shadow">
                  <div className="h-5 w-5 rounded-full border-2 border-violet-600 border-t-transparent animate-spin" />
                  <span className="text-sm font-medium text-slate-700">Procesando cambios...</span>
                </div>
              </div>
            )}
            <div className="bg-violet-700 text-white px-5 py-3 rounded-t-xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Logo superior - {logoModalOptica.nombre}</h3>
              <button
                onClick={cerrarModalLogo}
                disabled={saving}
                className="text-white/80 hover:text-white text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg border border-violet-100 bg-violet-50 px-3 py-2 text-xs text-violet-900">
                Podés configurar logos distintos: uno para la barra superior y otro para la pestaña del navegador.
              </div>

              <div className="flex items-center justify-center h-28 bg-slate-100 rounded-lg border">
                {logoModalOptica.logo_url ? (
                  <img src={resolveLogoUrl(logoModalOptica.logo_url)} alt="Logo actual" className="h-24 object-contain" />
                ) : (
                  <span className="text-slate-400 text-sm">Sin logo cargado</span>
                )}
              </div>

              <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Escala del logo</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0.7"
                      max="1.6"
                      step="0.05"
                      value={logoScaleInput}
                      onChange={(e) => setLogoScaleInput(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-xs font-semibold text-slate-700 w-14 text-right">
                      {Math.round(logoScaleInput * 100)}%
                    </span>
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={guardarEscalaLogo}
                      disabled={saving}
                      className="px-3 py-1.5 bg-violet-600 text-white text-sm rounded hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
                    >
                      {saving ? 'Guardando...' : 'Guardar tamaño'}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Ajusta el tamaño máximo dentro de la barra de navegación.</p>
                </div>

                <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subir archivo de imagen</label>
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => setSelectedLogoFile(e.target.files?.[0] || null)}
                    className="text-xs flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={subirLogoOptica}
                    disabled={saving || !selectedLogoFile}
                    className="px-3 py-1.5 bg-violet-600 text-white text-sm rounded hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {saving ? 'Subiendo...' : 'Subir'}
                  </button>
                </div>
                {selectedLogoFile && (
                  <p className="text-xs text-slate-500 mt-1">{selectedLogoFile.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">O ingresar URL del logo</label>
                <div className="flex gap-2">
                  <input
                    className="border rounded px-2 py-1.5 text-sm flex-1 min-w-0"
                    placeholder="https://... o /assets/images/logo.png"
                    value={logoUrlInput}
                    onChange={(e) => setLogoUrlInput(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={guardarLogoUrl}
                    disabled={saving}
                    className="px-3 py-1.5 bg-slate-600 text-white text-sm rounded hover:bg-slate-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {saving ? 'Guardando...' : 'Guardar logos'}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Logo de pestaña (tab/favicon) opcional</label>
                <input
                  className="border rounded px-2 py-1.5 text-sm w-full"
                  placeholder="https://... o /assets/images/favicon.png"
                  value={tabLogoUrlInput}
                  onChange={(e) => setTabLogoUrlInput(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">Si está vacío, la pestaña usará el mismo logo superior.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Escala del logo de pestaña</label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0.8"
                    max="1.8"
                    step="0.05"
                    value={tabLogoScaleInput}
                    onChange={(e) => setTabLogoScaleInput(Number(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-xs font-semibold text-slate-700 w-14 text-right">
                    {Math.round(tabLogoScaleInput * 100)}%
                  </span>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={guardarEscalaLogoTab}
                    disabled={saving}
                    className="px-3 py-1.5 bg-violet-600 text-white text-sm rounded hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {saving ? 'Guardando...' : 'Guardar tamaño pestaña'}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Ajusta el tamaño visual del ícono en la pestaña del navegador.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subir archivo para logo de pestaña</label>
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/x-icon"
                    onChange={(e) => setSelectedTabLogoFile(e.target.files?.[0] || null)}
                    className="text-xs flex-1 min-w-0"
                  />
                  <button
                    type="button"
                    onClick={subirLogoTabOptica}
                    disabled={saving || !selectedTabLogoFile}
                    className="px-3 py-1.5 bg-violet-600 text-white text-sm rounded hover:bg-violet-700 disabled:opacity-60 whitespace-nowrap"
                  >
                    {saving ? 'Subiendo...' : 'Subir pestaña'}
                  </button>
                </div>
                {selectedTabLogoFile && (
                  <p className="text-xs text-slate-500 mt-1">{selectedTabLogoFile.name}</p>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <button
                  onClick={cerrarModalLogo}
                  disabled={saving}
                  className="px-4 py-2 bg-slate-100 rounded text-sm hover:bg-slate-200 disabled:opacity-60"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
